/**
 * src/admin-ui/src/components/utility.styles.ts
 *
 * Shared styles for route, error and diagnostic utility components.
 */

import { Alert, Box, Button, Container, Paper, Typography } from '@mui/material';
import { Error as ErrorIcon } from '@mui/icons-material';
import { styled } from '@mui/material/styles';

export const FullScreenState = styled(Box)(({ theme }) => ({
  display: 'flex',
  minHeight: '100vh',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(2),
  padding: theme.spacing(2),
}));

export const DialogTitleRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

export const DialogInfoAlert = styled(Alert)(({ theme }) => ({
  marginBottom: theme.spacing(3),
}));

export const DialogSection = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(3),
}));

export const WrapRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1),
  flexWrap: 'wrap',
}));

export const DialogActionsRow = styled(WrapRow)(({ theme }) => ({
  gap: theme.spacing(2),
  [theme.breakpoints.down('sm')]: {
    '& .MuiButton-root': {
      width: '100%',
    },
  },
}));

export const CodePaper = styled(Paper)(({ theme }) => ({
  overflow: 'auto',
  padding: theme.spacing(2),
  backgroundColor: theme.palette.action.hover,
  overflowWrap: 'anywhere',
}));

export const CodeBlock = styled('pre')(({ theme }) => ({
  overflow: 'auto',
  padding: theme.spacing(2),
  backgroundColor: theme.palette.action.hover,
  fontFamily: 'monospace',
  fontSize: '0.8rem',
  whiteSpace: 'pre-wrap',
}));

export const DiagnosticOverlay = styled('aside')(({ theme }) => ({
  position: 'fixed',
  right: theme.spacing(1),
  bottom: theme.spacing(9),
  zIndex: theme.zIndex.tooltip,
  maxWidth: 300,
  maxHeight: 200,
  overflow: 'auto',
  padding: theme.spacing(1.25),
  backgroundColor: theme.palette.grey[900],
  color: theme.palette.common.white,
  fontFamily: 'monospace',
  fontSize: 10,
  [theme.breakpoints.down('sm')]: {
    right: theme.spacing(1),
    left: theme.spacing(1),
    maxWidth: 'none',
  },
}));

export const DiagnosticTitle = styled('div')(({ theme }) => ({
  marginBottom: theme.spacing(0.5),
  fontWeight: 700,
}));

export const DiagnosticError = styled('div')(({ theme }) => ({
  color: theme.palette.error.light,
}));

export const RedirectOverlay = styled('aside')(({ theme }) => ({
  position: 'fixed',
  top: theme.spacing(1),
  right: theme.spacing(1),
  zIndex: theme.zIndex.tooltip,
  maxWidth: 'calc(100vw - 16px)',
  overflowWrap: 'anywhere',
  padding: theme.spacing(1.25),
  backgroundColor: theme.palette.error.main,
  color: theme.palette.error.contrastText,
  fontSize: 12,
}));

export const StopRedirectButton = styled(Button)(({ theme }) => ({
  marginTop: theme.spacing(1),
  backgroundColor: theme.palette.error.contrastText,
  color: theme.palette.error.main,
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

export const ErrorPageContainer = styled(Container)(({ theme }) => ({
  paddingTop: theme.spacing(4),
}));

export const ErrorCard = styled(Paper)(({ theme }) => ({
  display: 'flex',
  width: '100%',
  maxWidth: 500,
  flexDirection: 'column',
  alignItems: 'center',
  padding: theme.spacing(4),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2),
  },
}));

export const LargeErrorIcon = styled(ErrorIcon)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  color: theme.palette.error.main,
  fontSize: 64,
}));

export const CenteredMessage = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  textAlign: 'center',
}));

export const ErrorDetails = styled(Box)(({ theme }) => ({
  width: '100%',
  marginBottom: theme.spacing(3),
  padding: theme.spacing(2),
  backgroundColor: theme.palette.action.hover,
}));

export const ReloadButton = styled(Button)(({ theme }) => ({
  marginBottom: theme.spacing(2),
}));
